import { usePlanningStore } from '@planning/AppProvider'
import { InitializationWrapper } from '@planning/components/InitializationWrapper'
import { useAsyncFetch } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'

import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { TallyclerkInitialSelection } from './TallyclerkInitialSelection'

export interface TallyGangOption {
  id: number
  name: string
}

interface Props {
  onContinueClick: () => void
  gangs: TallyGangOption[]
  onFetchGangs: () => void
}

export const TallyclerkHomePage = observer(({ onContinueClick, gangs, onFetchGangs }: Props) => {
  const {
    tallymanSelectVesselAndOperationViewStore: store,
    vesselVisitQueryService,
    vesselVisitItemStore,
    railVisitQueryService,
    railVisitItemStore,
    containerDamageReportViewStore,
    orderItemStore,
    containerItemStore,
  } = usePlanningStore()
  if (!store.railTallyStore || !store.vesselTallyStore) return <></>

  const [params] = useSearchParams()
  const { from, to } = useMemo(() => {
    const from = moment()
      .subtract(params.get('days') ?? 30, 'd') // days param is for supporting e2e tests
      .toDate()

    const to = moment().endOf('d').toDate()

    return { from, to }
  }, [params])

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        vesselVisitQueryService.fetch({ from, to }),
        railVisitQueryService.fetch({ from, to }),
        containerDamageReportViewStore.fetch(),
      ])
    }

    fetchData()
  }, [vesselVisitQueryService, railVisitQueryService, containerDamageReportViewStore, from, to])

  const [isInitializedVesselOrders, setInitializedVesselOrders] = useState(false)
  useAsyncFetch(async () => {
    // without this check, there will be excessive order fetching, since the DataManager will fetch carrier visits for other orders
    if (vesselVisitItemStore.hasBeenInitialized && !isInitializedVesselOrders) {
      if (vesselVisitItemStore.vesselVisitIds.length) {
        await orderItemStore.fetchByVisitIds([...vesselVisitItemStore.vesselVisitIds])
        await containerItemStore.fetchByVisitIds([...vesselVisitItemStore.vesselVisitIds])
      }
      setInitializedVesselOrders(true)
    }
  }, [
    vesselVisitItemStore.vesselVisitIds,
    vesselVisitItemStore.hasBeenInitialized,
    orderItemStore,
    containerItemStore,
    isInitializedVesselOrders,
  ])

  const [isInitializedRailOrders, setInitializedRailOrders] = useState(false)
  useAsyncFetch(async () => {
    // without this check, there will be excessive order fetching, since the DataManager will fetch carrier visits for other orders
    if (railVisitItemStore.hasBeenInitialized && !isInitializedRailOrders) {
      if (railVisitItemStore.railVisitIds.length) {
        await orderItemStore.fetchByVisitIds([...railVisitItemStore.railVisitIds])
        await containerItemStore.fetchByVisitIds([...railVisitItemStore.railVisitIds])
      }
      setInitializedRailOrders(true)
    }
  }, [
    railVisitItemStore.railVisitIds,
    railVisitItemStore.hasBeenInitialized,
    orderItemStore,
    containerItemStore,
    isInitializedRailOrders,
  ])

  const isInitializing =
    !store ||
    !store.railTallyStore ||
    !store.vesselTallyStore ||
    !store.railTallyStore ||
    !railVisitItemStore.hasBeenInitialized ||
    !isInitializedVesselOrders ||
    !isInitializedRailOrders

  return (
    <InitializationWrapper isInitializing={isInitializing}>
      <TallyclerkInitialSelection
        onContinueClick={onContinueClick}
        gangs={gangs}
        onFetchGangs={onFetchGangs}
      />
    </InitializationWrapper>
  )
})
